import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { Icon, TgHeader, TabBar } from "../components/ui/index.js";
import {
  SHOP_ORDER_STATUS_LABELS_RU_CLIENT,
  formatDateShort as formatDate,
  type Order,
  type ShopOrderStatus,
} from "@mnp/shared";
import {
  FlexStatusPill,
  PackagingBadge,
  PreorderWarnBlock,
  OrderCardSkeleton,
  preorderMissingLabel,
} from "../components/flex-form/OrderAtoms.js";
import { flagFor } from "../components/flex-form/FlexFormAtoms.js";

// Лёгкая модель shop-заказа для списка. Полный объект (с items) тянем
// только когда юзер проваливается в /shop/order/:id.
type ShopOrderListItem = {
  id: string;
  shortCode: string;
  status: ShopOrderStatus;
  totalPrice: string | number;
  giftPump: boolean;
  createdAt: string;
};

// Order from /orders may now be direction="flex" with flex* fields
// surfaced by the serializer (see apps/api/src/lib/orderSerializer.ts).
// Shared Order.direction теперь string (контракт-чистка волны 2 PR6) —
// локально доращиваем только flex*-поля сериализатора.
type FlexOrderExt = Order & {
  flexRequestNumber?: string | null;
  flexOriginCountry?: string | null;
  flexDestinationCountry?: string | null;
  flexRecipientName?: string | null;
  flexRecipientCity?: string | null;
  flexRecipientMissingPhone?: boolean | null;
  flexRecipientMissingAddress?: boolean | null;
  flexHadPackaging?: boolean | null;
  flexPieces?: number | null;
  flexDeclaredPriceEur?: number | string | null;
};

type AnyListItem =
  | { kind: "order"; data: FlexOrderExt }
  | { kind: "shop"; data: ShopOrderListItem };

type FilterKey = "all" | "flex" | "shop";

export function OrdersScreen() {
  const [filter, setFilter] = useState<FilterKey>("all");
  const nav = useNavigate();

  // Unified orders fetch — backend may still return non-flex `Order` rows
  // (легаси Ozon/WB до PR4 серии удаления), but the client renders flex only:
  // модуль Ozon/WB в архиве (archive/ozonwb-flow).
  const {
    data: orders,
    isLoading: ordersLoading,
    isError: ordersError,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: ["orders"],
    queryFn: () => api<FlexOrderExt[]>("/orders"),
  });

  const {
    data: shopOrders,
    isLoading: shopLoading,
    isError: shopError,
    refetch: refetchShop,
  } = useQuery({
    queryKey: ["shop-orders"],
    queryFn: () => api<ShopOrderListItem[]>("/shop/orders"),
  });

  const isLoading = ordersLoading || shopLoading;
  const isError = ordersError || shopError;

  // Only flex orders have client UI; non-flex rows (легаси Ozon/WB) are
  // filtered out entirely.
  const flexOrders = (orders ?? []).filter((o) => o.direction === "flex");
  const shopList = shopOrders ?? [];

  // Apply chip filter. "all" merges everything; otherwise we slice.
  let visibleOrders: FlexOrderExt[] = [];
  let visibleShop: ShopOrderListItem[] = [];
  if (filter === "all") {
    visibleOrders = flexOrders;
    visibleShop = shopList;
  } else if (filter === "flex") {
    visibleOrders = flexOrders;
  } else if (filter === "shop") {
    visibleShop = shopList;
  }

  const orderItems: AnyListItem[] = visibleOrders.map((o) => ({ kind: "order" as const, data: o }));
  const shopItems: AnyListItem[] = visibleShop.map((s) => ({ kind: "shop" as const, data: s }));
  const merged: AnyListItem[] = [...orderItems, ...shopItems].sort((a, b) => {
    const ta = new Date(a.data.createdAt).getTime();
    const tb = new Date(b.data.createdAt).getTime();
    return tb - ta;
  });

  const chips: Array<{ k: FilterKey; l: string; n: number }> = [
    { k: "all", l: "Все", n: flexOrders.length + shopList.length },
    { k: "flex", l: "Отправки", n: flexOrders.length },
    { k: "shop", l: "Упаковка", n: shopList.length },
  ];

  return (
    <div className="app-shell">
      <TgHeader title="Мои заказы" />
      <div className="screen pad" style={{ paddingTop: 12 }}>
        {/* Filter chips — horizontally scrollable */}
        <div
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            margin: "0 -16px 14px",
            padding: "0 16px",
            scrollbarWidth: "none",
          }}
        >
          {chips.map((c) => (
            <button
              key={c.k}
              className="chip"
              data-active={filter === c.k}
              style={{ flexShrink: 0 }}
              onClick={() => setFilter(c.k)}
            >
              {c.l}
              {c.n > 0 && <span style={{ opacity: 0.7, marginLeft: 4 }}>{c.n}</span>}
            </button>
          ))}
        </div>

        {/* Error toast */}
        {isError && !isLoading && (
          <div
            className="banner banner-danger mb-3"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <Icon name="alertCircle" size={18} />
            <div style={{ flex: 1 }}>Не удалось загрузить заказы</div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                refetchOrders();
                refetchShop();
              }}
            >
              Обновить
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && !isError && (
          <>
            <OrderCardSkeleton />
            <OrderCardSkeleton />
            <OrderCardSkeleton />
          </>
        )}

        {/* Empty state */}
        {!isLoading && !isError && merged.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div
              style={{
                background: "var(--mnp-teal-50)",
                borderRadius: "50%",
                width: 80,
                height: 80,
                display: "grid",
                placeItems: "center",
                margin: "0 auto 16px",
                color: "var(--mnp-teal-700)",
              }}
            >
              <Icon name="box" size={36} />
            </div>
            <h3 className="t-h3">Пока заказов нет</h3>
            <p className="t-body mt-1" style={{ marginBottom: 16 }}>
              Создайте первую отправку — это займёт пару минут.
            </p>
            <button className="btn btn-primary" onClick={() => nav("/new-parcel")}>
              Новая отправка
            </button>
          </div>
        )}

        {/* List */}
        {!isLoading && !isError && merged.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {merged.map((it) =>
              it.kind === "order" ? (
                <FlexOrderCard key={"f-" + it.data.id} o={it.data} />
              ) : (
                <ShopOrderCard key={"s-" + it.data.id} o={it.data} />
              ),
            )}
          </div>
        )}
      </div>
      <TabBar />
    </div>
  );
}

// BUG-08: correct Russian plural for «место/места/мест» — the old `=== 1`
// ternary produced «5 места», «11 места» etc.
function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

// ─── Flex card (RR-XXXXXXXX + flags + status pill + optional warn) ──
function FlexOrderCard({ o }: { o: FlexOrderExt }) {
  const nav = useNavigate();
  const isPreOrder =
    o.status === "pending_recipient_data" ||
    o.flexRecipientMissingPhone ||
    o.flexRecipientMissingAddress;

  const goDetail = () => nav(`/orders/${o.id}`);
  const goFill = () => nav(`/orders/${o.id}/edit-recipient`);

  const num = o.flexRequestNumber ?? "RR-" + o.id.slice(0, 8).toUpperCase();
  const recipientPreview = o.flexRecipientName ?? "Получатель не указан";
  const cityPreview = o.flexRecipientCity ?? "";
  const meta =
    "Создано " +
    formatDate(o.createdAt) +
    (o.flexPieces ? ` · ${o.flexPieces} ${pluralRu(o.flexPieces, "место", "места", "мест")}` : "");

  const sumStr =
    o.flexDeclaredPriceEur != null ? `${Number(o.flexDeclaredPriceEur)} EUR` : "";

  return (
    <div
      className="card card-tight"
      onClick={goDetail}
      style={{
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        borderLeft: isPreOrder ? "3px solid var(--mnp-red)" : undefined,
        paddingLeft: isPreOrder ? 13 : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
          {isPreOrder && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "var(--mnp-red)",
                boxShadow: "0 0 0 3px rgba(213,40,46,.18)",
                flexShrink: 0,
              }}
            />
          )}
          <span style={{ fontSize: 18, lineHeight: 1 }}>{flagFor(o.flexOriginCountry ?? "")}</span>
          <Icon name="arrowRight" size={12} color="var(--mnp-fg-3)" />
          <span style={{ fontSize: 18, lineHeight: 1 }}>{flagFor(o.flexDestinationCountry ?? "")}</span>
          <span
            style={{
              fontFamily: "var(--mnp-font-mono)",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--mnp-fg-2)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {num}
          </span>
          {o.flexHadPackaging && <PackagingBadge />}
        </div>
        {sumStr && (
          <span
            style={{
              fontFamily: "var(--mnp-font-display)",
              fontWeight: 800,
              fontSize: 14,
              color: "var(--mnp-fg-1)",
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {sumStr}
          </span>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: 13,
            color: "var(--mnp-fg-1)",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          {recipientPreview}
        </span>
        <FlexStatusPill status={o.status} />
      </div>
      {(cityPreview || meta) && (
        <p style={{ margin: 0, fontSize: 11, color: "var(--mnp-fg-3)" }}>
          {[cityPreview, meta].filter(Boolean).join(" · ")}
        </p>
      )}

      {isPreOrder && (
        <PreorderWarnBlock
          text={preorderMissingLabel({
            missingPhone: o.flexRecipientMissingPhone,
            missingAddress: o.flexRecipientMissingAddress,
          })}
          onClick={goFill}
        />
      )}
    </div>
  );
}

function ShopOrderCard({ o }: { o: ShopOrderListItem }) {
  const nav = useNavigate();
  const isReady = o.status === "ready";
  const isPending = o.status === "pending";
  const isPaid = o.status === "paid";
  const isAwaitingPayment = o.status === "awaiting_payment";
  const label = SHOP_ORDER_STATUS_LABELS_RU_CLIENT[o.status] ?? o.status;

  return (
    <div
      className="card card-tight"
      onClick={() => nav(`/shop/order/${o.id}`)}
      style={{
        cursor: "pointer",
        borderColor: isReady ? "var(--mnp-teal-600)" : "var(--mnp-border-soft)",
        ...(isReady ? { background: "var(--mnp-teal-50)" } : null),
        position: "relative",
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "var(--mnp-teal-100)",
              color: "var(--mnp-teal-700)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon name="box" size={14} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Упаковка</span>
          <span className="t-small">· {formatDate(o.createdAt)}</span>
        </div>
        <span
          style={{ fontFamily: "var(--mnp-font-mono)", fontSize: 11, color: "var(--mnp-fg-3)" }}
        >
          #{o.shortCode}
        </span>
      </div>
      <p className="t-small mb-2" style={{ color: "var(--mnp-fg-2)" }}>
        Расходники · {Number(o.totalPrice)} ₾{o.giftPump ? " · насос в подарок" : ""}
      </p>
      <div className="flex items-center justify-between">
        <span className={"badge status-" + o.status}>{label}</span>
        {(isReady || isPending || isPaid || isAwaitingPayment) && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: "var(--mnp-teal-700)",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <Icon name="qr" size={14} /> {isReady ? "Показать QR" : "Открыть"}
          </span>
        )}
      </div>
    </div>
  );
}
